import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useConfirm } from 'material-ui-confirm';
import {
  Avatar,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Stack,
  SxProps,
  Tooltip,
  Typography,
  ChipProps,
  Divider,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Circle as CircleIcon,
  Info as InfoIcon,
  MoreHoriz as MoreHorizIcon,
} from '@mui/icons-material';
import moment from 'moment';

import {
  ISection,
  LESSON_TYPES,
  deleteCourseZoomSectionByIdAsAdmin,
  getSectionsState,
} from '../../../../../store/V2/SectionSlice';
import { getAuthState } from '../../../../../store/AuthSlice';
import { TP_COLORS } from '../../../../../constants';
import { useAppSelector } from '../../../../../hooks';
import { ILessonHistory } from '../../../../../store/V2/LessonHistorySlice';
import {
  TClassJoinStatusData,
  checkUserPermission,
  getClassJoinStatusData,
  getClassShortUrlByWaitingId,
} from '../../../../../helper';
import ViewZoomRecordingsModal from '../../../../../components/Modals/ViewZoomRecordingsModal';
import ClassJoinTimeStatusViewerPopperComponent from '../../components/ClassJoinTimeStatusViewerPopperComponent';
import {
  TMeetingRequestStatus,
  MEETING_REQUEST_STATUSES,
  MEETING_REQUEST_TYPES,
} from '../../../../../store/V2/MeetingRequestsSlice';
import FeedbacksModal from '../../../../../components/V2/Feedback/admin/FeedbacksModal';
import ViewAttendanceHistoryModal from '../modals/ViewAttendanceHistoryModal';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../store/V2/RolesSlice';
import { IUser } from '../../../../../store/UserSlice';
import { getTenantsState } from '../../../../../store/V2/TenantsSlice';

interface ZoomSectionItemProps {
  orderNumber: number;
  section: ISection;
  isOneToOne: boolean;
  onCancelClass: (section: ISection) => void;
  onRescheduleClass: (section: ISection) => void;
  onUpdateClass: (section: ISection) => void;
}

const classLinkButtonStyles: SxProps = {
  backgroundColor: '#3498DB',
  color: TP_COLORS.white,
  p: '8px',
  borderRadius: '5px',
  fontSize: '12px',
  fontWeight: 600,
};

const containerStyles: SxProps = {
  backgroundColor: 'transparent',
  borderRadius: '10px',
  border: '1px dashed #CBD5E1',
};

const classOptionsDropDownButtonStyles: SxProps = {
  backgroundColor: '#E2E8F0',
  color: '#000000',
  borderRadius: '5px',
  padding: '5px',
};

const classOptionsMenuStyles: SxProps = {
  fontSize: '14px',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: 'transparent',
  },
};

const getZoomRequestStatusColor = (status: TMeetingRequestStatus) => {
  let color: ChipProps['color'] = 'default';

  switch (status) {
    case MEETING_REQUEST_STATUSES.APPROVED:
      color = 'success';
      break;

    case MEETING_REQUEST_STATUSES.REJECTED:
      color = 'error';
      break;

    case MEETING_REQUEST_STATUSES.PENDING:
      color = 'warning';
      break;

    default:
      color = 'default';
      break;
  }

  return color;
};

const CustomCircleIcon = (
  <CircleIcon sx={{ width: '7px', height: '7px', color: '#CBD5E1' }} />
);

const MenuDividerLine = (
  <Divider sx={{ borderColor: '#CBD5E1', borderStyle: 'dashed' }} />
);

const ZoomSectionItem: React.FC<ZoomSectionItemProps> = ({
  orderNumber,
  section,
  isOneToOne,
  onCancelClass,
  onRescheduleClass,
  onUpdateClass,
}) => {
  const confirm = useConfirm();
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);
  const sectionsState = useAppSelector(getSectionsState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;

  // State
  const [iconAnchorEl, setIconAnchorEl] = useState<HTMLElement | null>(null);
  const [isFeedbacksModalOpen, setFeedbackModalOpen] = useState(false);
  const [
    isViewZoomRecordingsModalOpen,
    setViewZoomRecordingsModalOpen,
  ] = useState(false);

  const [
    classOptionsAnchorEl,
    setClassOptionsAnchorEl,
  ] = useState<HTMLElement | null>(null);

  const [
    isAttendanceHistoryModalOpen,
    setAttendanceHistoryModalOpen,
  ] = useState(false);

  let formatedDuration = '';
  let studentJoinData: TClassJoinStatusData | undefined;
  let tutorJoinData: TClassJoinStatusData | undefined;
  let isZoomLinkVisible = false;
  let tutorName = '';

  const lessonHistory = section.lessonHistory as ILessonHistory | undefined;
  const popOverId = `${section._id}-mouse-over-popover`;
  const isPopOverOpen = Boolean(iconAnchorEl);
  const formatedDateTime = section.dateTime
    ? moment(section.dateTime).format('ddd MMM DD YYYY hh:mm A')
    : '';

  const isExpired = moment(section.dateTime) < moment();
  const isZoomRequestCanceled =
    section.zoomRequest &&
    section.zoomRequest.type === MEETING_REQUEST_TYPES.CANCEL &&
    section.zoomRequest.status === MEETING_REQUEST_STATUSES.APPROVED;

  const isZoomRequestNotPending = !(
    section.zoomRequest &&
    section.zoomRequest.status === MEETING_REQUEST_STATUSES.PENDING
  );

  const classCancellationPermission =
    !isExpired &&
    !isZoomRequestCanceled &&
    isZoomRequestNotPending &&
    checkUserPermission({
      authUser,
      tenant: appTenant,
      permissions: {
        adminSchedule: [USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_EDIT],
      },
    });

  const classReschedulePermission =
    isOneToOne &&
    !isExpired &&
    !isZoomRequestCanceled &&
    isZoomRequestNotPending &&
    checkUserPermission({
      authUser,
      tenant: appTenant,
      permissions: {
        adminSchedule: [USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_EDIT],
      },
    });

  const classUpdatePermission =
    !isExpired &&
    !isZoomRequestCanceled &&
    isZoomRequestNotPending &&
    checkUserPermission({
      authUser,
      tenant: appTenant,
      permissions: {
        adminSchedule: [USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_EDIT],
      },
    });

  if (!isZoomRequestCanceled && isZoomRequestNotPending) {
    isZoomLinkVisible = true;
  }

  const classOptionsMenuId = `${section.eventId}-class-options-menu`;
  const classOptionsMenuButtonId = `${section.eventId}-class-options-menu-button`;
  const isClassOptionsMenuOpen = Boolean(classOptionsAnchorEl);
  const isClassOptionsDropDownVisible =
    (classCancellationPermission ||
      classReschedulePermission ||
      classUpdatePermission) &&
    section.lessonType !== LESSON_TYPES.TRIAL &&
    section.lessonType !== LESSON_TYPES.DUMMY;

  if (section.duration) {
    const _hours = Math.floor(section.duration / 60);
    const _mins = section.duration % 60;

    if (_hours) {
      formatedDuration = `${_hours} hr`;

      if (_mins) {
        formatedDuration += ` ${_mins} min`;
      }
    } else {
      formatedDuration += ` ${_mins} min`;
    }
  }

  if (section.dateTime) {
    studentJoinData = getClassJoinStatusData(
      section.dateTime,
      section.studentJoinTime
    );

    tutorJoinData = getClassJoinStatusData(
      section.dateTime,
      section.tutorJoinTime
    );
  }

  if (section.tutor) {
    const tutorData = section.tutor as IUser;

    tutorName = `${tutorData.firstname || ''} ${
      tutorData.lastname || ''
    }`.trim();
  }

  const handleDelete = () => {
    confirm({
      title: 'Are you sure?',
      description: 'Do you want to delete this section?',
    }).then(() => {
      dispatch(deleteCourseZoomSectionByIdAsAdmin(section._id));
    });
  };

  const toggleViewZoomRecordingsModal = () => {
    setViewZoomRecordingsModalOpen((prevValue) => !prevValue);
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setIconAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setIconAnchorEl(null);
  };

  const openClassOptionsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setClassOptionsAnchorEl(event.currentTarget);
  };

  const closeClassOptionsMenu = () => {
    setClassOptionsAnchorEl(null);
  };

  const handleCancelClass = () => {
    onCancelClass(section);
    closeClassOptionsMenu();
  };

  const handleRescheduleClass = () => {
    onRescheduleClass(section);
    closeClassOptionsMenu();
  };

  const handleUpdateClass = () => {
    onUpdateClass(section);
    closeClassOptionsMenu();
  };

  const toggleFeedbacksModal = () => {
    setFeedbackModalOpen((prevValue) => !prevValue);
  };

  const toggleAttendanceHistoryModal = () => {
    setAttendanceHistoryModalOpen((prevValue) => !prevValue);
  };

  return (
    <>
      <Card elevation={0} sx={containerStyles}>
        <CardContent sx={{ p: '16px' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" alignItems="center" flexGrow={1} mr="15px">
              <Avatar
                sx={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: '#1C9FF7',
                  mr: '15px',
                }}
              >
                {orderNumber}
              </Avatar>
              <Stack gap={1}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Chip
                    size="small"
                    label="Zoom"
                    color="info"
                    sx={{ width: 'fit-content' }}
                  />
                  {tutorName ? (
                    <Chip
                      size="small"
                      label={`Tutor: ${tutorName}`}
                      color="default"
                      sx={{ width: 'fit-content' }}
                    />
                  ) : null}
                  {section.zoomRequest ? (
                    <Chip
                      size="small"
                      label={`${section.zoomRequest.type} - ${section.zoomRequest.status}`}
                      color={getZoomRequestStatusColor(
                        section.zoomRequest.status
                      )}
                      sx={{ width: 'fit-content' }}
                    />
                  ) : null}
                </Stack>
                <Typography
                  title={section.title}
                  variant="subtitle2"
                  fontWeight={600}
                  className="ellipsis-text one-line"
                  maxWidth="calc(100% - 20px)"
                >
                  {section.title}
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                >
                  <Typography fontWeight={400} fontSize="14px" color="#1E293B">
                    {formatedDateTime}
                  </Typography>
                  {CustomCircleIcon}
                  <Typography fontWeight={400} fontSize="14px" color="#1E293B">
                    <b>Duration:</b> {formatedDuration}
                  </Typography>
                  {lessonHistory ? (
                    <>
                      {CustomCircleIcon}
                      <ButtonBase
                        onClick={toggleFeedbacksModal}
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#01A79F',
                          textDecoration: 'underline',
                        }}
                      >
                        Feedbacks
                      </ButtonBase>
                    </>
                  ) : null}
                  {section.zoomRecordings && section.zoomRecordings.length ? (
                    <>
                      {CustomCircleIcon}
                      <ButtonBase
                        onClick={toggleViewZoomRecordingsModal}
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#1E293B',
                          textDecoration: 'underline',
                        }}
                      >
                        Recordings
                      </ButtonBase>
                    </>
                  ) : null}
                  {lessonHistory &&
                  lessonHistory.attendanceHistory &&
                  lessonHistory.attendanceHistory.length ? (
                    <>
                      {CustomCircleIcon}
                      <ButtonBase
                        onClick={toggleAttendanceHistoryModal}
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#1E293B',
                          textDecoration: 'underline',
                        }}
                      >
                        Attendance
                      </ButtonBase>
                    </>
                  ) : null}
                  {section.dateTime && isExpired ? (
                    <>
                      {CustomCircleIcon}
                      <Typography
                        fontSize="14px"
                        fontWeight={500}
                        color="info.main"
                        display="flex"
                        alignItems="center"
                        aria-owns={isPopOverOpen ? popOverId : undefined}
                        aria-haspopup="true"
                        onMouseEnter={handlePopoverOpen}
                        onMouseLeave={handlePopoverClose}
                      >
                        User&apos;s Joined Times
                        <>
                          &nbsp;
                          <InfoIcon
                            sx={{
                              color: '#000000',
                              width: '15px',
                              height: '15px',
                            }}
                          />
                        </>
                      </Typography>
                    </>
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              {section.studentWaitingId && isZoomLinkVisible ? (
                <ButtonBase
                  href={getClassShortUrlByWaitingId(section.studentWaitingId)}
                  target="_blank"
                  sx={classLinkButtonStyles}
                >
                  Student Link
                </ButtonBase>
              ) : null}
              {section.tutorWaitingId && isZoomLinkVisible ? (
                <ButtonBase
                  href={getClassShortUrlByWaitingId(section.tutorWaitingId)}
                  target="_blank"
                  sx={classLinkButtonStyles}
                >
                  Tutor Link
                </ButtonBase>
              ) : null}
              {isClassOptionsDropDownVisible ? (
                <Tooltip title="Rescheduling & Cancellation" placement="top">
                  <ButtonBase
                    aria-haspopup="true"
                    id={classOptionsMenuButtonId}
                    sx={classOptionsDropDownButtonStyles}
                    onClick={openClassOptionsMenu}
                    aria-controls={
                      isClassOptionsMenuOpen ? classOptionsMenuId : undefined
                    }
                    aria-expanded={isClassOptionsMenuOpen ? 'true' : undefined}
                  >
                    <MoreHorizIcon />
                  </ButtonBase>
                </Tooltip>
              ) : null}
              {checkUserPermission({
                authUser,
                tenant: appTenant,
                permissions: {
                  adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
                },
              }) ? (
                <Tooltip title="Delete" placement="top">
                  <IconButton
                    color="error"
                    onClick={handleDelete}
                    disabled={sectionsState.status === 'loading'}
                    sx={{ ml: '-8px' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      {/* MODALS */}
      {isFeedbacksModalOpen && lessonHistory ? (
        <FeedbacksModal
          open={isFeedbacksModalOpen}
          onClose={toggleFeedbacksModal}
          lessonHistoryId={lessonHistory._id}
        />
      ) : null}
      {isViewZoomRecordingsModalOpen ? (
        <ViewZoomRecordingsModal
          open={isViewZoomRecordingsModalOpen}
          onClose={toggleViewZoomRecordingsModal}
          zoomRecordings={section.zoomRecordings || []}
        />
      ) : null}
      {isAttendanceHistoryModalOpen ? (
        <ViewAttendanceHistoryModal
          open={isAttendanceHistoryModalOpen}
          onClose={toggleAttendanceHistoryModal}
          list={lessonHistory?.attendanceHistory || []}
        />
      ) : null}
      <Popover
        disableRestoreFocus
        id={popOverId}
        open={isPopOverOpen}
        anchorEl={iconAnchorEl}
        onClose={handlePopoverClose}
        PaperProps={{
          elevation: 0,
          sx: {
            p: '15px',
            borderRadius: '20px',
            border: '1px solid #E2E8F0',
            boxShadow: '0px 14px 40px 0px #6E9DB22E',
            maxWidth: '260px',
          },
        }}
        sx={{
          pointerEvents: 'none',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <ClassJoinTimeStatusViewerPopperComponent
          tutorJoinData={tutorJoinData}
          studentJoinData={studentJoinData}
          tutorJoinTime={section.tutorJoinTime}
          studentJoinTime={section.studentJoinTime}
        />
      </Popover>
      <Menu
        id={classOptionsMenuId}
        anchorEl={classOptionsAnchorEl}
        open={isClassOptionsMenuOpen}
        onClose={closeClassOptionsMenu}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              borderRadius: '10px',
              border: '1px solid #CBD5E1',
            },
          },
        }}
        MenuListProps={{
          'aria-labelledby': classOptionsMenuButtonId,
        }}
      >
        {classUpdatePermission ? (
          <MenuItem
            onClick={handleUpdateClass}
            sx={{ ...classOptionsMenuStyles }}
          >
            Update Class
          </MenuItem>
        ) : null}
        {classReschedulePermission ? (
          <>
            <MenuItem
              onClick={handleRescheduleClass}
              sx={{ ...classOptionsMenuStyles }}
            >
              Reschedule Class
            </MenuItem>
          </>
        ) : null}
        {classCancellationPermission ? (
          <>
            {classUpdatePermission || classReschedulePermission
              ? MenuDividerLine
              : null}
            <MenuItem
              onClick={handleCancelClass}
              sx={{ ...classOptionsMenuStyles, color: 'error.main' }}
            >
              Cancel Class
            </MenuItem>
          </>
        ) : null}
      </Menu>
    </>
  );
};

export default ZoomSectionItem;
